import { useState, useEffect, useRef } from 'react';
import api, { API_HOST } from '../../utils/api';
import { useToast } from '../shared/Toast';
import BrandPreview from './BrandPreview';

const COUNTRY_OPTIONS = [
  'United States', 'Canada', 'United Kingdom', 'Ireland', 'Australia', 'New Zealand',
  'Germany', 'France', 'Netherlands', 'Spain', 'Italy', 'South Africa', 'India', 'Mexico', 'Brazil',
];

const FONT_OPTIONS = [
  'Inter', 'Open Sans', 'Roboto', 'Lato', 'Montserrat', 'Poppins',
  'Raleway', 'Nunito', 'Source Sans Pro', 'PT Sans', 'Merriweather',
  'Playfair Display', 'Oswald', 'Ubuntu', 'Rubik'
];

export default function PartnerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pullingHubspot, setPullingHubspot] = useState(false);
  const fileInputRef = useRef();
  const { addToast } = useToast();

  const handlePullHubspot = async () => {
    setPullingHubspot(true);
    try {
      const { data } = await api.get('/partners/me/hubspot-details');
      // Fill only the fields HubSpot returned; don't wipe existing values.
      setProfile(prev => ({
        ...prev,
        companyName: data.companyName || prev.companyName,
        websiteUrl: data.websiteUrl || prev.websiteUrl,
        companyEmail: data.companyEmail || prev.companyEmail,
        companyPhone: data.companyPhone || prev.companyPhone,
        companyAddress: data.companyAddress || prev.companyAddress,
        country: data.country || prev.country,
      }));
      const got = ['companyEmail', 'companyPhone', 'companyAddress'].filter(k => data[k]).length;
      addToast(got ? 'Pulled details from HubSpot — review and Save' : 'HubSpot returned no extra contact details', got ? 'success' : 'error');
    } catch (err) {
      addToast(err.response?.data?.error || 'Could not pull from HubSpot', 'error');
    } finally {
      setPullingHubspot(false);
    }
  };

  useEffect(() => {
    api.get('/partners/me').then(({ data }) => {
      setProfile(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        companyName: profile.companyName,
        businessDescription: profile.businessDescription,
        websiteUrl: profile.websiteUrl,
        companyEmail: profile.companyEmail,
        companyPhone: profile.companyPhone,
        companyAddress: profile.companyAddress,
        ctaUrl: profile.ctaUrl,
        country: profile.country,
        targetIndustries: profile.targetIndustries,
        servicesOffered: profile.servicesOffered,
        certifications: profile.certifications,
        testimonial: profile.testimonial,
        tagline: profile.tagline,
        brandColors: profile.brandColors,
        brandFonts: profile.brandFonts,
      };
      // Only include logoPath in save if it looks like a URL (otherwise it's a file path
      // managed by the upload endpoint)
      if (profile.logoPath !== undefined && (profile.logoPath === '' || /^https?:\/\//i.test(profile.logoPath || ''))) {
        payload.logoPath = profile.logoPath;
      }
      const { data } = await api.put('/partners/me', payload);
      setProfile(data);
      addToast('Profile saved successfully');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await api.post('/partners/me/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, logoPath: data.logoPath }));
      addToast('Logo uploaded successfully');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to upload logo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const updateColor = (key, value) => {
    setProfile(prev => ({
      ...prev,
      brandColors: { ...prev.brandColors, [key]: value }
    }));
  };

  const updateFont = (key, value) => {
    setProfile(prev => ({
      ...prev,
      brandFonts: { ...prev.brandFonts, [key]: value }
    }));
  };

  // Profile completeness
  const getCompleteness = () => {
    if (!profile) return 0;
    let score = 0;
    if (profile.companyName) score += 20;
    if (profile.businessDescription) score += 25;
    if (profile.logoPath) score += 25;
    if (profile.websiteUrl) score += 10;
    if (profile.tagline) score += 10;
    if (profile.brandColors?.primary) score += 5;
    if (profile.brandFonts?.heading) score += 5;
    return score;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;
  if (!profile) return <div className="text-red-500">Failed to load profile</div>;

  const completeness = getCompleteness();

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Brand Profile</h1>
      <p className="text-gray-500 mb-6">Set up your brand so generated assets match your identity.</p>

      {/* Completeness bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-gray-700">Profile Completeness</span>
          <span className={completeness >= 70 ? 'text-green-600' : 'text-yellow-600'}>{completeness}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className={`h-2 rounded-full transition-all ${completeness >= 70 ? 'bg-green-500' : 'bg-yellow-500'}`} style={{ width: `${completeness}%` }} />
        </div>
        {completeness < 70 && <p className="text-xs text-gray-500 mt-2">Complete your company name, description, and logo to generate assets.</p>}
      </div>

      <div className="space-y-6">
        {/* Company Name */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
          <input
            type="text"
            value={profile.companyName || ''}
            onChange={e => setProfile(prev => ({ ...prev, companyName: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
          />
        </div>

        {/* Business Description */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Description *</label>
          <textarea
            value={profile.businessDescription || ''}
            onChange={e => setProfile(prev => ({ ...prev, businessDescription: e.target.value }))}
            rows={6}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none resize-vertical"
            placeholder="Describe your business, services, and what makes you different. Include your target audience, key offerings, and any specialties."
          />
          <p className="text-xs text-gray-400 mt-2">
            Not sure what to write? Try asking an AI tool: "Give me a comprehensive overview of my business based on [your website URL]. Include what we do, who we serve, our key differentiators, and our value proposition." Then paste the result here.
          </p>
        </div>

        {/* Website & Tagline */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
            <input
              type="url"
              value={profile.websiteUrl || ''}
              onChange={e => setProfile(prev => ({ ...prev, websiteUrl: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
              placeholder="https://yourcompany.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <input
              type="text"
              value={profile.tagline || ''}
              onChange={e => setProfile(prev => ({ ...prev, tagline: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
              placeholder="Your IT, Simplified"
            />
          </div>
        </div>

        {/* Company Contact Details */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Company Contact Details</h3>
            <button
              onClick={handlePullHubspot}
              disabled={pullingHubspot}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-[#ff7a59] text-[#ff7a59] hover:bg-[#fff3f0] disabled:opacity-50"
              title="Fill these from your linked HubSpot contact & company"
            >
              {pullingHubspot ? 'Pulling…' : 'Pull from HubSpot'}
            </button>
          </div>
          <p className="text-xs text-gray-400 mb-4">Used in generated assets (headers, footers, signatures, CTAs) so the AI uses your real details instead of guessing.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Email</label>
              <input
                type="email"
                value={profile.companyEmail || ''}
                onChange={e => setProfile(prev => ({ ...prev, companyEmail: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="hello@yourcompany.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                value={profile.companyPhone || ''}
                onChange={e => setProfile(prev => ({ ...prev, companyPhone: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
              <input
                type="text"
                value={profile.companyAddress || ''}
                onChange={e => setProfile(prev => ({ ...prev, companyAddress: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="123 Main St, Suite 100, City, State ZIP"
              />
            </div>
          </div>
        </div>

        {/* Audience & Proof */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-1">Audience & Proof</h3>
          <p className="text-xs text-gray-400 mb-4">Helps the AI tailor your assets. It never invents facts about your business, so anything you add here (like certifications or a testimonial) is what it can use as proof.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Call-to-Action Link</label>
              <input
                type="url"
                value={profile.ctaUrl || ''}
                onChange={e => setProfile(prev => ({ ...prev, ctaUrl: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="https://yourcompany.com/book-a-call"
              />
              <p className="text-xs text-gray-400 mt-1">Where buttons send people (booking page or contact form). Defaults to your website.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                list="country-options"
                value={profile.country || ''}
                onChange={e => setProfile(prev => ({ ...prev, country: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="United States"
              />
              <datalist id="country-options">
                {COUNTRY_OPTIONS.map(c => <option key={c} value={c} />)}
              </datalist>
              <p className="text-xs text-gray-400 mt-1">Sets spelling, currency, paper size, and email compliance wording.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Industries</label>
              <input
                type="text"
                value={profile.targetIndustries || ''}
                onChange={e => setProfile(prev => ({ ...prev, targetIndustries: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="Accounting, legal, construction"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certifications & Credentials</label>
              <input
                type="text"
                value={profile.certifications || ''}
                onChange={e => setProfile(prev => ({ ...prev, certifications: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                placeholder="Microsoft Solutions Partner, CompTIA Security+"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Services You Offer</label>
              <textarea
                value={profile.servicesOffered || ''}
                onChange={e => setProfile(prev => ({ ...prev, servicesOffered: e.target.value }))}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none resize-vertical"
                placeholder="Managed IT, cloud desktops, cybersecurity, backup and recovery, help desk"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Testimonial</label>
              <textarea
                value={profile.testimonial || ''}
                onChange={e => setProfile(prev => ({ ...prev, testimonial: e.target.value }))}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none resize-vertical"
                placeholder={'"Moving to cloud desktops cut our IT headaches in half." Jane Doe, Office Manager, Doe Accounting'}
              />
              <p className="text-xs text-gray-400 mt-1">A real quote with the person's name, title, and company. Used word for word as proof in one-pagers, service pages, and decks. Only add quotes you have permission to use.</p>
            </div>
          </div>
        </div>

        {/* Brand Colors */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Brand Colors</h3>
          <div className="grid grid-cols-3 gap-4">
            {[['primary', 'Primary'], ['secondary', 'Secondary'], ['accent', 'Accent']].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={profile.brandColors?.[key] || '#000000'}
                    onChange={e => updateColor(key, e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                  />
                  <input
                    type="text"
                    value={profile.brandColors?.[key] || ''}
                    onChange={e => updateColor(key, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                    placeholder="#000000"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Brand Fonts */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Brand Fonts</h3>
          <div className="grid grid-cols-2 gap-4">
            {[['heading', 'Heading Font'], ['body', 'Body Font']].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <select
                  value={profile.brandFonts?.[key] || ''}
                  onChange={e => updateFont(key, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue focus:border-transparent outline-none"
                >
                  <option value="">Select a font</option>
                  {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Logo Upload */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Logo *</h3>
          {profile.logoPath && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg inline-block">
              <img
                src={/^https?:\/\//i.test(profile.logoPath) ? profile.logoPath : `${API_HOST}/${profile.logoPath}`}
                alt="Logo"
                className="max-h-20 max-w-[240px] object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="text-xs text-gray-400 mt-2 max-w-[260px] break-all">{profile.logoPath}</div>
            </div>
          )}

          {/* Option A: Upload */}
          <div className="mb-4">
            <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Option A — Upload a file</div>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-v2-blue transition-colors"
            >
              <p className="text-gray-500 text-sm">{uploadingLogo ? 'Uploading...' : 'Click to upload PNG or SVG (max 3MB)'}</p>
            </div>
            <input ref={fileInputRef} type="file" accept=".png,.svg" onChange={handleLogoUpload} className="hidden" />
          </div>

          {/* Option B: URL */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Option B — Paste a logo URL</div>
            <input
              type="url"
              value={profile.logoPath && /^https?:\/\//i.test(profile.logoPath) ? profile.logoPath : ''}
              onChange={e => setProfile(prev => ({ ...prev, logoPath: e.target.value }))}
              placeholder="https://yourcompany.com/logo.png"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Use a publicly accessible URL hosting your logo (e.g., from your own website). Click "Save Profile" below to apply.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving} className="px-6 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          <button onClick={() => setShowPreview(true)} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">
            Preview Brand
          </button>
        </div>
      </div>

      {showPreview && <BrandPreview profile={profile} onClose={() => setShowPreview(false)} />}
    </div>
  );
}
